<script setup lang="ts">
import SecondaryButton from '@/packages/ui/src/Buttons/SecondaryButton.vue';
import { PlusIcon } from '@heroicons/vue/16/solid';
import InvoiceTableRow from './InvoiceTableRow.vue';
import InvoiceTableHeading from './InvoiceTableHeading.vue';
import { router } from '@inertiajs/vue3';
import type { InvoiceIndexEntry } from '@/packages/api/src';
import { computed, ref, watch } from 'vue';
import Pagination from '@/packages/ui/src/Pagination.vue';
import {
    getCoreRowModel,
    getSortedRowModel,
    useVueTable,
    type SortingState,
} from '@tanstack/vue-table';

export type SortColumn = 'reference' | 'recipient' | 'status' | 'date' | 'due_at' | 'paid_date';
export type SortDirection = 'asc' | 'desc';

const props = defineProps<{
    invoices: InvoiceIndexEntry[];
    sortColumn: SortColumn;
    sortDirection: SortDirection;
}>();

const emit = defineEmits<{
    sort: [column: SortColumn, direction: SortDirection];
}>();

const sorting = computed<SortingState>(() => [
    {
        id: props.sortColumn,
        desc: props.sortDirection === 'desc',
    },
    ...(props.sortColumn !== 'reference' ? [{ id: 'reference', desc: false }] : []),
]);

const columns = computed(() => [
    {
        id: 'reference',
        accessorFn: (row: InvoiceIndexEntry) => row.reference.toLowerCase(),
    },
    {
        id: 'recipient',
        accessorFn: (row: InvoiceIndexEntry) => row.recipient.toLowerCase(),
    },
    {
        id: 'status',
        accessorFn: (row: InvoiceIndexEntry) => row.status_label.toLowerCase(),
    },
    {
        id: 'date',
        sortDescFirst: true,
        accessorFn: (row: InvoiceIndexEntry) => row.date,
    },
    {
        id: 'due_at',
        sortDescFirst: true,
        sortUndefined: 'last' as const,
        accessorFn: (row: InvoiceIndexEntry) => row.due_at || undefined,
    },
    {
        id: 'paid_date',
        sortDescFirst: true,
        sortUndefined: 'last' as const,
        accessorFn: (row: InvoiceIndexEntry) => row.paid_date || undefined,
    },
]);

const descFirstColumns = new Set<SortColumn>(
    columns.value.filter((column) => column.sortDescFirst).map((column) => column.id as SortColumn)
);

function handleSort(column: SortColumn) {
    if (props.sortColumn === column) {
        emit('sort', column, props.sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
        emit('sort', column, descFirstColumns.has(column) ? 'desc' : 'asc');
    }
}

const table = useVueTable({
    get data() {
        return props.invoices;
    },
    get columns() {
        return columns.value;
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
        get sorting() {
            return sorting.value;
        },
    },
    manualSorting: false,
});

const sortedInvoices = computed(() => {
    return table.getRowModel().rows.map((row) => row.original);
});

const PAGE_SIZE = 15;
const currentPage = ref(1);

watch([() => props.sortColumn, () => props.sortDirection, () => props.invoices], () => {
    currentPage.value = 1;
});

const paginatedInvoices = computed(() => {
    const start = (currentPage.value - 1) * PAGE_SIZE;
    return sortedInvoices.value.slice(start, start + PAGE_SIZE);
});

function createInvoice() {
    router.visit('/invoices/create');
}
</script>

<template>
    <div class="flow-root">
        <div class="inline-block min-w-full align-middle">
            <div
                data-testid="invoice_table"
                class="grid min-w-full"
                style="grid-template-columns: minmax(150px, auto) 1fr 130px 150px 150px 150px 80px 80px">
                <InvoiceTableHeading
                    :sort-column="props.sortColumn"
                    :sort-direction="props.sortDirection"
                    @sort="handleSort"></InvoiceTableHeading>
                <div v-if="!sortedInvoices.length" class="col-span-8 py-24 text-center">
                    <h3 class="text-text-primary font-semibold">No invoices found</h3>
                    <p class="pb-5">Create your first invoice now!</p>
                    <SecondaryButton :icon="PlusIcon" @click="createInvoice"
                        >Create your First Invoice</SecondaryButton
                    >
                </div>
                <template v-for="invoice in paginatedInvoices" :key="invoice.id">
                    <InvoiceTableRow :invoice="invoice"></InvoiceTableRow>
                </template>
            </div>
        </div>
    </div>
    <Pagination
        v-model:page="currentPage"
        :total="sortedInvoices.length"
        :items-per-page="PAGE_SIZE"></Pagination>
</template>
